import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Button from "components/basic/button";
import AutoComplete from "components/basic/autoComplete";
import Typography from "components/basic/typography";

const PAGE_SIZE_OPTIONS = [
  { id: 10, label: "10" },
  { id: 25, label: "25" },
  { id: 50, label: "50" },
];

interface MeetingsPaginationProps {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalRows: number;
  onPrev: () => void;
  onNext: () => void;
  onPageSize: (size: number) => void;
}

const MeetingsPagination: React.FC<MeetingsPaginationProps> = ({
  currentPage,
  totalPages,
  pageSize,
  totalRows,
  onPrev,
  onNext,
  onPageSize,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("meetings");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
        pt: 1.5,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Typography
        variant="caption"
        style={{ color: theme.palette.text.secondary }}
      >
        {totalRows === 0
          ? t("pagination.none")
          : t("pagination.showing", {
              from: (currentPage - 1) * pageSize + 1,
              to: Math.min(currentPage * pageSize, totalRows),
              total: totalRows,
            })}
      </Typography>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            variant="outlined"
            color="primary"
            disabled={currentPage <= 1}
            onClick={onPrev}
            startAdornment={<ChevronLeftIcon fontSize="small" />}
          >
            {t("pagination.prev")}
          </Button>
          <Typography variant="caption">
            {currentPage} / {totalPages}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            disabled={currentPage >= totalPages}
            onClick={onNext}
            endAdornment={<ChevronRightIcon fontSize="small" />}
          >
            {t("pagination.next")}
          </Button>
        </Stack>
        <Box sx={{ width: 90 }}>
          <AutoComplete
            options={PAGE_SIZE_OPTIONS}
            value={pageSize}
            onChange={(v) => {
              if (v) {
                onPageSize(v.id);
              }
            }}
          />
        </Box>
      </Stack>
    </Box>
  );
};

export default MeetingsPagination;
